import React, { useContext, useEffect, useState } from 'react'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import OButton from 'components/reusable/OButton'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import dayjs from 'dayjs'
import VideoList from './VideoList'
import VideoView from './VideoView'
import VideoEdit from './VideoEdit'
import Pagination from '../Pagination'
import AuthContext from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import helpers from 'utils/helpers'
import OSearch from 'components/reusable/OSearch'
import { statusOptions } from 'utils/constants'
import CustomSelect from 'components/reusable/CustomSelect'

const VideoManagement = () => {
  const { user, updatePageName } = useContext(AuthContext)
  const navigate = useNavigate()
  const manager =
    user?.permission?.find(e => e.manager === 'video-management') ?? {}
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  const [videoList, setVideoList] = useState([])
  const [videoEdit, setVideoEdit] = useState(false)
  const [currentItem, setCurrentItem] = useState('')
  const [videoView, setVideoView] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [filterData, setFilterData] = useState({
    status: '',
    searchKey: '',
    startDate: '',
    endDate: '',
    isReset: false,
    isFilter: false
  })
  const [sort, setSort] = useState({
    sortBy: 'createdAt',
    sortType: 'desc'
  })

  const handleEdit = item => {
    setCurrentItem(item)
    setVideoEdit(!videoEdit)
    navigate('/video-management/edit', { state: item })
  }

  const handleView = item => {
    setCurrentItem(item)
    setVideoView(!videoView)
    navigate('/video-management/view', { state: item })
  }

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData,
      setFilterData,
      setPage
    })
  }, [debouncedSearchTerm])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchTerm])

  useEffect(() => {
    updatePageName(t('NAV_VIDEOS'))
  }, [])

  const getVideos = async () => {
    try {
      setIsLoading(true)
      const { status, startDate, endDate, searchKey, isFilter } = filterData

      const payload = {
        page,
        pageSize,
        status: status,
        startDate: helpers?.ternaryCondition(
          startDate,
          dayjs(startDate).format('YYYY-MM-DD'),
          null
        ),
        endDate: helpers?.ternaryCondition(
          endDate,
          dayjs(endDate).format('YYYY-MM-DD'),
          null
        ),
        keyword: searchKey,
        sortBy: sort.sortBy,
        sortType: sort.sortType
      }
      const path = apiPath.video
      const result = await apiGet(path, payload)
      const responseData = result?.data?.results
      const resultStatus = result?.data?.success
      setVideoList(responseData?.docs)
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, responseData.page, null),
        pageCount: helpers?.ternaryCondition(
          resultStatus,
          responseData.totalPages,
          null
        ),
        perPageItem: helpers?.ternaryCondition(
          resultStatus,
          responseData?.docs.length,
          null
        ),
        totalItems: helpers?.ternaryCondition(
          resultStatus,
          responseData.totalDocs,
          null
        )
      })
    } catch (error) {
      console.log('error in get all video list==>>>>', error.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getVideos()
  }, [page, filterData, sort])

  const handleReset = () => {
    setFilterData({
      status: '',
      searchKey: '',
      endDate: '',
      startDate: '',
      isReset: true,
      isFilter: false
    })
    setPage(1)
    setSearchTerm('')
  }

  const handleDateChange = (start, end) => {
    setPage(1)
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true
    })
  }

  const statusPage = e => {
    setFilterData({ ...filterData, status: e.target.value, isFilter: true })
    setPage(1)
  }

  return (
    <>
      <div className='bg-[#F9F9F9]'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9] rounded-lg'>
            <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3'>
              <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
                <div className='flex items-center lg:pt-0 pt-3 flex-wrap'>
                  <div className='relative flex items-center mb-3'>
                    <OSearch
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      placeholder={t('SEARCH_BY_TITLE')}
                    />
                  </div>
                  <ODateRangePicker
                    handleDateChange={handleDateChange}
                    isReset={filterData?.isReset}
                    setIsReset={setFilterData}
                  />
                  <CustomSelect
                    id='status'
                    name='status'
                    value={filterData.status}
                    onChange={statusPage}
                    options={statusOptions}
                    placeholder='O_ALL'
                    t={t}
                  />
                  <OButton
                    label='Reset'
                    type='button'
                    onClick={handleReset}
                    extraClasses='ml-3 mb-3'
                    style={{ borderRadius: '0.5rem' }}
                  />
                </div>
              </div>
              <div className='flex items-center justify-end px-4 ms-auto mb-3'>
                {(manager?.add || user?.role === 'admin') && (
                  <button
                    title='Add video'
                    type='button'
                    className='bg-LightBlue flex text-sm px-8 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap'
                    onClick={() => navigate('/video-management/add')}
                  >
                    + {t('ADD_VIDEO')}
                  </button>
                )}
              </div>
            </form>
            
            <VideoList
              videoList={videoList}
              getVideos={getVideos}
              page={page}
              handleEdit={handleEdit}
              handleView={handleView}
              currentItem={currentItem}
              videoView={videoView}
              setSort={setSort}
              sort={sort}
              manager={manager}
              isLoading={isLoading}
            />

            {videoView && (
              <VideoView
                videoView={videoView}
                currentItem={currentItem}
                handleView={handleView}
              />
            )}

            {videoEdit && (
              <VideoEdit
                handleEdit={handleEdit}
                currentItem={currentItem}
                getVideos={getVideos}
              />
            )}
            
            {paginationObj?.totalItems ? (
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                page={page}
              />
            ) : (null)}
          </div>
        </div>
      </div>
    </>
  )
}

export default VideoManagement